import type { Deployment } from '@/api/services/deployments';

/**
 * What a deployment row can say about itself without a liveness probe.
 *
 * An IaC deployment's `status` is `active` as soon as its package exists, even
 * though nobody has applied it. This maps the row to a state that does
 * not overclaim: a finished package is `packageReady`, never "active". Whether
 * the host is actually pushing metrics is only known from the per-deployment
 * liveness check on its dashboard.
 */
export type DeploymentState =
  | 'generating'
  | 'packageReady'
  | 'failed'
  | 'cancelled'
  | 'stopped'
  | 'active'
  | 'unknown';

export const DEPLOYMENT_STATES: readonly DeploymentState[] = [
  'generating',
  'packageReady',
  'failed',
  'cancelled',
  'stopped',
  'active',
  'unknown',
];

/** Search param on a project's Deployments and Dashboard tabs naming the
 *  selected deployment, so it can be linked to directly. */
export const DEPLOYMENT_PARAM = 'deployment';

function projectTabPath(tab: string, projectId: string, deploymentId?: string): string {
  const base = `/dashboard/projects/${encodeURIComponent(projectId)}/${tab}`;
  return deploymentId ? `${base}?${DEPLOYMENT_PARAM}=${encodeURIComponent(deploymentId)}` : base;
}

/** A project's Dashboard tab, optionally with one deployment selected. */
export function projectDashboardPath(projectId: string, deploymentId?: string): string {
  return projectTabPath('dashboard', projectId, deploymentId);
}

/** A project's Deployments (setup) tab, optionally with one deployment
 *  selected. Unlike `projectDeploymentsPath` in the launcher lib, it does not
 *  vet the ids: callers pass ids that came from the API. */
export function projectDeploymentsTabPath(projectId: string, deploymentId?: string): string {
  return projectTabPath('deployments', projectId, deploymentId);
}

const GENERATING = new Set(['pending', 'generating', 'packaging', 'uploading']);

type StateFields = Pick<Deployment, 'status' | 'generation_status' | 'deployment_type'>;

export function deploymentState(d: StateFields): DeploymentState {
  const status = (d.status ?? '').toLowerCase();
  if (status === 'stopped' || status === 'terminated') return 'stopped';

  const generation = (d.generation_status ?? '').toLowerCase();
  if (generation) {
    if (GENERATING.has(generation)) return 'generating';
    if (generation === 'failed') return 'failed';
    if (generation === 'cancelled') return 'cancelled';
    if (generation === 'completed') return 'packageReady';
  }

  // Rows without a generation lifecycle (the legacy "connect" flow).
  if (status === 'failed' || status === 'error') return 'failed';
  if (status === 'active' || status === 'running') return 'active';
  if (GENERATING.has(status)) return 'generating';
  return 'unknown';
}

/** Count of each state, in `DEPLOYMENT_STATES` order, zero counts dropped. */
export function summarizeStates(items: StateFields[]): Array<[DeploymentState, number]> {
  const counts = new Map<DeploymentState, number>();
  for (const d of items) {
    const s = deploymentState(d);
    counts.set(s, (counts.get(s) ?? 0) + 1);
  }
  return DEPLOYMENT_STATES.filter((s) => counts.has(s)).map((s) => [s, counts.get(s)!]);
}

type TargetFields = {
  cloud_provider?: string | null;
  instance_type?: string | null;
  region?: string | null;
};

const norm = (v: string | null | undefined) => (v ?? '').trim();

/**
 * Same rule as the backend's `DeploymentTarget` (ADR-0015): a project holds
 * one deployment per provider, instance and region. Provider compares
 * case-insensitively; a missing region equals an empty one.
 */
export function isSameTarget(a: TargetFields, b: TargetFields): boolean {
  return (
    norm(a.cloud_provider).toLowerCase() === norm(b.cloud_provider).toLowerCase() &&
    norm(a.instance_type) === norm(b.instance_type) &&
    norm(a.region) === norm(b.region)
  );
}

/** "AWS g5.xlarge · us-east-1": the target, for labels next to a name. */
export function targetLabel(d: TargetFields): string {
  const host = [norm(d.cloud_provider), norm(d.instance_type)].filter(Boolean).join(' ');
  return [host, norm(d.region)].filter(Boolean).join(' · ');
}
